import type React from 'react';
import { type RefObject, useEffect, useState } from 'react';

export const usePagination = (
    ref: RefObject<HTMLElement | null>,
    children: React.ReactElement[] | React.ReactElement,
    className: string
) => {
    const [activePage, setActivePage] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [dummyWidth, setDummyWidth] = useState(0);
    const [cardsPerPage, setCardsPerPage] = useState(1);

    const calcTotalPages = () => {
        const { current } = ref;

        if (!current || !className) {
            return;
        }

        const slides = current.querySelectorAll(className);
        if (slides.length) {
            const cardWidth = (slides[0] as HTMLElement).offsetWidth;
            const perPage = Math.floor(current.clientWidth / cardWidth) || 1;
            setCardsPerPage(perPage);
            const totalPages = Math.ceil(slides.length / perPage);
            setPageCount(totalPages);
        }
    };

    const calcDummyWidth = () => {
        const { current } = ref;
        if (current && className) {
            const slides = current.querySelectorAll(className);
            if (slides.length > 0) {
                const firstElement = slides[0] as HTMLElement;
                setDummyWidth(current.clientWidth - firstElement.clientWidth);
            }
        }
    };

    useEffect(() => {
        const { current } = ref;
        let observer: IntersectionObserver;

        if (current && className) {
            const slides = current.querySelectorAll(className);
            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && entry.intersectionRatio >= 0.99) {
                            const index = Array.from(slides).indexOf(entry.target);
                            setActivePage(index);
                        }
                    });
                },
                {
                    root: current,
                    threshold: 0.99
                }
            );
            slides.forEach((slide) => observer.observe(slide));
        }

        return () => {
            observer?.disconnect();
        };
    }, []);

    useEffect(() => {
        const { current } = ref;

        if (current) {
            calcTotalPages();
            calcDummyWidth();
            window.addEventListener('resize', calcTotalPages);
            window.addEventListener('resize', calcDummyWidth);

            return () => {
                window.removeEventListener('resize', calcTotalPages);
                window.removeEventListener('resize', calcDummyWidth);
            };
        }
    }, [children]);

    const goToPage = (newPage: any) => {
        if (!ref.current || !className) {
            return;
        }

        const slides = ref.current.querySelectorAll(className);

        if (slides.length > newPage) {
            const targetSlide = slides[newPage] as HTMLElement;
            ref.current.scrollTo({
                left: targetSlide.offsetLeft,
                behavior: 'smooth'
            });
        }
    };

    const navigateSlideByDirection = (direction: number) => {
        if (!ref.current || !className) {
            return;
        }

        const newPage = activePage + direction;
        const slides = ref.current.querySelectorAll(className);

        if (slides.length > newPage && newPage >= 0) {
            const targetSlide = slides[newPage] as HTMLElement;
            if (targetSlide) {
                ref.current.scrollTo({
                    left: targetSlide.offsetLeft,
                    behavior: 'smooth'
                });
            }
        } else if (direction === 1 && newPage >= slides.length) {
            // on last slide and "next" pressed: scroll to end so dummy space is consumed and next arrow hides
            ref.current.scrollTo({
                left: ref.current.scrollWidth - ref.current.clientWidth,
                behavior: 'smooth'
            });
        }
    };

    return {
        pageCount,
        dummyWidth,
        activePage,
        goToPage,
        navigateSlideByDirection,
        cardsPerPage
    };
};
